import { combineReducers, createAction, createSelector, on, props } from '@ngrx/store';
import { CoreState } from '@app/core/store/core.slice';
import {
  initializeLoadStateWithoutData,
  LoadState,
} from '@app/core/store/_prototypes/action-reducers/state/load.state';
import { MyWorkHistoryItem } from '@app/core/types/work/work.types';
import { ExpenseDetail, ExpenseItem, ExpenseToCreate, ProjectOptions } from '@app/expenses/model/expense.types';
import { createLoadActions } from '@app/core/store/_prototypes/action-reducers/actions/load.actions';
import { createLoadReducer } from '@app/core/store/_prototypes/action-reducers/reducers/load.reducers';
import { loadSelector } from '@app/core/store/_prototypes/action-reducers/selectors/load.selectors';
import { HolidayRequest } from '@app/core/types/work/requests.types';

const PREFIX = '[WORK]';
export const stateKey = 'work';

export interface WorkState {
  workHistory: LoadState<Record<never, never>, MyWorkHistoryItem[]>;
  expenses: {
    expensesForMonthOfYear: LoadState<{ month: number; year: number }, ExpenseItem[]>;
    details: LoadState<{ expenseId: string }, ExpenseDetail>;
    delete: LoadState<{ expenseId: string }, void>;
    create: {
      projects: LoadState<Record<never, never>, ProjectOptions[]>;
      calculatedAmount: LoadState<{ projectId: string; expenseType: string; quantity: number }, number>;
      submit: LoadState<{ expenseToCreate: ExpenseToCreate }, void>;
    };
  };
  nextPaymentDate: LoadState<Record<never, never>, string>;
  requests: {
    holidayRequests: LoadState<Record<never, never>, HolidayRequest[]>;
    submit: LoadState<{ holidayRequest: HolidayRequest }, void>;
  };
}

export const getWorkHistory = createLoadActions<Record<string, never>, MyWorkHistoryItem[]>(
  `${PREFIX} getWorkHistory`,
  props<Record<string, never>>()
);
export const getNextPaymentDate = createLoadActions<Record<string, never>, string>(
  `${PREFIX} getNextPaymentDate`,
  props<Record<string, never>>()
);
export const getExpenseForMonthOfYear = createLoadActions<{ month: number; year: number }, ExpenseItem[]>(
  `${PREFIX} getExpenseForMonthOfYear`,
  props<{ month: number; year: number }>()
);
export const getExpenseDetails = createLoadActions<{ expenseId: string }, ExpenseDetail>(
  `${PREFIX} getExpenseDetails`,
  props<{ expenseId: string }>()
);
export const deleteExpenseDetails = createLoadActions<{ expenseId: string }, void>(
  `${PREFIX} deleteExpenseDetails`,
  props<{ expenseId: string }>()
);
export const getProjects = createLoadActions<{ date: Date }, ProjectOptions[]>(
  `${PREFIX} getProjects`,
  props<{ date: Date }>()
);
export const calculateAmount = createLoadActions<
  { projectId: string; expenseType: string; date: Date; quantity: number },
  number
>(`${PREFIX} calculateAmount`, props<{ projectId: string; expenseType: string; date: Date; quantity: number }>());
export const createExpense = createLoadActions<{ expenseToCreate: ExpenseToCreate }, void>(
  `${PREFIX} createExpense`,
  props<{ expenseToCreate: ExpenseToCreate }>()
);
export const clearCalculatedAmount = createAction(`${PREFIX} clearCalculatedAmount`);
export const getHolidayRequests = createLoadActions<Record<string, never>, HolidayRequest[]>(
  `${PREFIX} getHolidayRequests`,
  props<Record<string, never>>()
);
export const createHolidayRequest = createLoadActions<{ holidayRequest: HolidayRequest }, void>(
  `${PREFIX} createExpense`,
  props<{ holidayRequest: HolidayRequest }>()
);

export const workReducers = combineReducers<WorkState>({
  workHistory: createLoadReducer(
    initializeLoadStateWithoutData<Record<string, never>, MyWorkHistoryItem[]>(),
    getWorkHistory
  ),
  nextPaymentDate: createLoadReducer(
    initializeLoadStateWithoutData<Record<string, never>, string>(),
    getNextPaymentDate
  ),
  expenses: combineReducers({
    expensesForMonthOfYear: createLoadReducer(
      initializeLoadStateWithoutData<{ month: number; year: number }, ExpenseItem[]>(),
      getExpenseForMonthOfYear,
      [
        on(deleteExpenseDetails.load.success, (state, action) => ({
          ...state,
          data: [...state?.data?.filter((expense) => expense.id !== action.loaded.expenseId)],
        })),
      ]
    ),
    details: createLoadReducer(
      initializeLoadStateWithoutData<{ expenseId: string }, ExpenseDetail>(),
      getExpenseDetails,
      [
        on(getExpenseDetails.load.failure, (state) => ({
          ...state,
          data: null,
        })),
      ]
    ),
    delete: createLoadReducer(initializeLoadStateWithoutData<{ expenseId: string }, void>(), deleteExpenseDetails),
    create: combineReducers({
      projects: createLoadReducer(initializeLoadStateWithoutData<{ date: Date }, ProjectOptions[]>(), getProjects),
      calculatedAmount: createLoadReducer(
        initializeLoadStateWithoutData<
          { projectId: string; expenseType: string; date: Date; quantity: number },
          number
        >(),
        calculateAmount,
        [
          on(clearCalculatedAmount, () =>
            initializeLoadStateWithoutData<
              { projectId: string; expenseType: string; date: Date; quantity: number },
              number
            >()
          ),
        ]
      ),
      submit: createLoadReducer(
        initializeLoadStateWithoutData<{ expenseToCreate: ExpenseToCreate }, void>(),
        createExpense
      ),
    }),
  }),
  requests: combineReducers({
    holidayRequests: createLoadReducer(
      initializeLoadStateWithoutData<Record<string, never>, HolidayRequest[]>(),
      getHolidayRequests
    ),
    submit: createLoadReducer(
      initializeLoadStateWithoutData<{ holidayRequest: HolidayRequest }, void>(),
      createHolidayRequest
    ),
  }),
});

const stateSelector = createSelector(
  (state: CoreState) => state,
  (state: CoreState) => state[stateKey] as WorkState
);

export const workHistoryStateSelector = createSelector(stateSelector, (state) => state.workHistory);
export const workHistoryState = loadSelector(workHistoryStateSelector);

export const nextPaymentDateStateSelector = createSelector(stateSelector, (state) => state.nextPaymentDate);
export const nextPaymentDateState = loadSelector(nextPaymentDateStateSelector);


const expensesStateSelector = createSelector(stateSelector, (state) => state.expenses);
export const createExpenseStateSelector = createSelector(expensesStateSelector, (state) => state.create);
export const projectsStateSelector = createSelector(createExpenseStateSelector, (state) => state.projects);
export const projectsState = loadSelector(projectsStateSelector);
export const calculatedAmountStateSelector = createSelector(
  createExpenseStateSelector,
  (state) => state.calculatedAmount
);
export const calculatedAmountState = loadSelector(calculatedAmountStateSelector);

export const requestsStateSelector = createSelector(stateSelector, (state) => state.requests);
export const holidayRequestStateSelector = createSelector(requestsStateSelector, (state) => state.holidayRequests);
export const holidayRequestState = loadSelector(holidayRequestStateSelector);
export const submitHolidayRequestStateSelector = createSelector(requestsStateSelector, (state) => state.submit);
export const submitHolidayRequestState = loadSelector(submitHolidayRequestStateSelector);
